import React, { useState, useEffect } from 'react';
import {
  ScheduledEvent,
  ScheduleType,
  SchedulePlatform,
  PodcastFormat,
  ScheduledGuest,
  GoogleCalendarConfig,
  NavigationScreen
} from '@/types';
import { SchedulerService } from '@/services/schedulerService';
import { GoogleService } from '@/services/googleService';
import {
  Calendar,
  CalendarDays,
  CalendarCheck,
  Clock,
  Users,
  Mic,
  Video,
  Plus,
  Trash2,
  Edit3,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  Download,
  Search,
  Filter,
  Sparkles,
  Radio,
  Play,
  Mail,
  X,
  ChevronLeft,
  ChevronRight,
  Settings,
  Bell,
  Send,
  Key,
  ShieldCheck,
  CheckCircle2
} from 'lucide-react';

interface SchedulerScreenProps {
  onNavigate: (screen: NavigationScreen) => void;
  onLaunchPodcastStudio?: (format?: 'vodcast-single' | 'vodcast-duo' | 'vodcast-online' | 'audio-only') => void;
  onLaunchGroupMeeting?: (roomCode?: string) => void;
}

export const SchedulerScreen: React.FC<SchedulerScreenProps> = ({
  onNavigate,
  onLaunchPodcastStudio,
  onLaunchGroupMeeting
}) => {
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [gcalConfig, setGcalConfig] = useState<GoogleCalendarConfig>(SchedulerService.getGoogleCalendarConfig());
  const [activeTab, setActiveTab] = useState<'all' | 'podcast' | 'meeting' | 'upcoming' | 'today'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [configTab, setConfigTab] = useState<'profile' | 'smtp' | 'automation'>('profile');
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState<ScheduledEvent | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState<string | null>(null);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [emailTestSuccess, setEmailTestSuccess] = useState<string | null>(null);
  const [smtpPasswordInput, setSmtpPasswordInput] = useState(() => GoogleService.getAccountInfo().emailConfig.smtpPassword || '••••••••••••••••');
  const [formSendEmailInvites, setFormSendEmailInvites] = useState(true);

  // Form Fields
  const [formType, setFormType] = useState<ScheduleType>('podcast');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDate, setFormDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [formTime, setFormTime] = useState('14:00');
  const [formDuration, setFormDuration] = useState<number>(45);
  const [formPlatform, setFormPlatform] = useState<SchedulePlatform>('podcast-studio');
  const [formPodcastFormat, setFormPodcastFormat] = useState<PodcastFormat>('vodcast-duo');
  const [formRoomCode, setFormRoomCode] = useState('');
  const [formCustomLink, setFormCustomLink] = useState('');
  const [formHostName, setFormHostName] = useState('MeetSplit Host');
  const [formHostEmail, setFormHostEmail] = useState('host@meetsplit.ai');
  const [formAutoRecord, setFormAutoRecord] = useState(true);
  const [formReminder, setFormReminder] = useState<number>(15);
  const [formSyncToGoogle, setFormSyncToGoogle] = useState(true);

  // Form Guest Manager
  const [formGuests, setFormGuests] = useState<ScheduledGuest[]>([]);
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestEmail, setNewGuestEmail] = useState('');
  const [newGuestRole, setNewGuestRole] = useState<'guest' | 'co-host' | 'attendee'>('guest');

  // Form Agenda Manager
  const [formAgenda, setFormAgenda] = useState<string[]>([]);
  const [newAgendaItem, setNewAgendaItem] = useState('');

  // Calendar View month navigation
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  // Load events and gcal config with real-time update listeners
  useEffect(() => {
    const refreshData = () => {
      setEvents(SchedulerService.getEvents());
      setGcalConfig(SchedulerService.getGoogleCalendarConfig());
    };
    refreshData();

    if (typeof window !== 'undefined') {
      window.addEventListener('meetsplit_schedule_changed', refreshData);
      window.addEventListener('storage', refreshData);
      return () => {
        window.removeEventListener('meetsplit_schedule_changed', refreshData);
        window.removeEventListener('storage', refreshData);
      };
    }
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3200);
  };

  const handleSyncGoogleCalendar = async () => {
    setIsSyncing(true);
    try {
      const res = await SchedulerService.syncAllWithGoogleCalendar();
      setEvents(SchedulerService.getEvents());
      setGcalConfig(SchedulerService.getGoogleCalendarConfig());
      showToast(`✓ Google Calendar synced successfully! (${res.syncedCount} events updated)`);
    } catch {
      showToast('❌ Google Calendar sync failed. Check internet connection.');
    } finally {
      setIsSyncing(false);
    }
  };

  const openCreateModal = (type: ScheduleType = 'podcast') => {
    setEditingEventId(null);
    setFormType(type);
    setFormTitle(type === 'podcast' ? 'Episode: The Deep Tech Brief' : 'Sprint Catchup & Demo');
    setFormDescription(
      type === 'podcast'
        ? 'Deep dive discussion with industry guest on creator tooling and local privacy.'
        : 'Team sprint status review, completed tasks, and upcoming milestones.'
    );
    const now = new Date();
    setFormDate(now.toISOString().split('T')[0]);
    setFormTime('15:00');
    setFormDuration(type === 'podcast' ? 60 : 30);
    setFormPlatform(type === 'podcast' ? 'podcast-studio' : 'meetsplit-room');
    setFormPodcastFormat('vodcast-duo');
    setFormRoomCode(
      type === 'podcast'
        ? `POD-${Math.floor(1000 + Math.random() * 9000)}`
        : `MS-${Math.floor(1000 + Math.random() * 9000)}`
    );
    setFormCustomLink('');
    setFormHostName(gcalConfig.accountName || 'MeetSplit Host');
    setFormHostEmail(gcalConfig.accountEmail || 'host@meetsplit.ai');
    setFormGuests([]);
    setFormAgenda(
      type === 'podcast'
        ? ['Introductions & Overview', 'Main Topic Discussion', 'Audience Questions', 'Wrap-up']
        : ['Completed deliverables', 'Live demo & testing', 'Blockers & Action items']
    );
    setFormAutoRecord(true);
    setFormReminder(15);
    setFormSyncToGoogle(true);
    setIsModalOpen(true);
  };

  const openEditModal = (event: ScheduledEvent) => {
    setEditingEventId(event.id);
    setFormType(event.type);
    setFormTitle(event.title);
    setFormDescription(event.description || '');

    const eventDate = new Date(event.startTime);
    setFormDate(eventDate.toISOString().split('T')[0]);
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');
    setFormTime(`${hours}:${minutes}`);

    setFormDuration(event.durationMinutes || 45);
    setFormPlatform(event.platform);
    setFormPodcastFormat(event.podcastFormat || 'vodcast-duo');
    setFormRoomCode(event.roomCode || '');
    setFormCustomLink(event.customLink || '');
    setFormHostName(event.hostName);
    setFormHostEmail(event.hostEmail);
    setFormGuests([...event.guests]);
    setFormAgenda([...(event.agenda || [])]);
    setFormAutoRecord(event.autoRecord ?? true);
    setFormReminder(event.reminderMinutes ?? 15);
    setFormSyncToGoogle(event.googleCalendarSynced ?? true);
    setIsModalOpen(true);
  };

  const handleAddGuest = () => {
    if (!newGuestEmail.trim()) return;
    const guest: ScheduledGuest = {
      id: `gst_${Date.now()}`,
      name: newGuestName.trim() || newGuestEmail.split('@')[0],
      email: newGuestEmail.trim(),
      role: newGuestRole
    };
    setFormGuests(prev => [...prev, guest]);
    setNewGuestName('');
    setNewGuestEmail('');
  };

  const handleRemoveGuest = (id: string) => {
    setFormGuests(prev => prev.filter(g => g.id !== id));
  };

  const handleAddAgendaItem = () => {
    if (!newAgendaItem.trim()) return;
    setFormAgenda(prev => [...prev, newAgendaItem.trim()]);
    setNewAgendaItem('');
  };

  const handleRemoveAgendaItem = (idx: number) => {
    setFormAgenda(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) {
      showToast('⚠️ Please enter an event title');
      return;
    }

    const startDateTime = new Date(`${formDate}T${formTime}:00`);
    const endDateTime = new Date(startDateTime.getTime() + formDuration * 60000);

    const eventPayload = {
      title: formTitle.trim(),
      type: formType,
      description: formDescription.trim(),
      agenda: formAgenda,
      startTime: startDateTime.toISOString(),
      endTime: endDateTime.toISOString(),
      durationMinutes: formDuration,
      hostName: formHostName.trim(),
      hostEmail: formHostEmail.trim(),
      guests: formGuests,
      platform: formPlatform,
      podcastFormat: formType === 'podcast' ? formPodcastFormat : undefined,
      roomCode: formRoomCode.trim() || undefined,
      customLink: formCustomLink.trim() || undefined,
      googleCalendarSynced: formSyncToGoogle,
      reminderMinutes: formReminder,
      autoRecord: formAutoRecord,
      status: 'upcoming' as const
    };

    if (editingEventId) {
      SchedulerService.updateEvent(editingEventId, eventPayload);
      showToast('✓ Scheduled event updated successfully');
    } else {
      const created = SchedulerService.addEvent(eventPayload);
      if (formSyncToGoogle) {
        SchedulerService.openInGoogleCalendar(created);
      }
      if (formSendEmailInvites && formGuests.length > 0) {
        SchedulerService.sendMeetingInvitesViaEmail(created);
      }
      showToast('✓ New session scheduled & synced to Google Calendar!');
    }

    setEvents(SchedulerService.getEvents());
    setIsModalOpen(false);
  };

  const handleSendEmailInvite = async (event: ScheduledEvent) => {
    if (!event.guests || event.guests.length === 0) {
      showToast('⚠️ No guest emails listed for this event. Add guests first!');
      return;
    }
    setIsSendingEmail(event.id);
    try {
      const res = await SchedulerService.sendMeetingInvitesViaEmail(event);
      setEvents(SchedulerService.getEvents());
      if (res.success) {
        showToast(`✓ Invites emailed to ${res.count} guest(s) via ${res.senderEmail}!`);
      } else {
        showToast(res.message);
      }
    } catch {
      showToast('Failed to dispatch emails. Please verify network connection.');
    } finally {
      setIsSendingEmail(null);
    }
  };

  const handleTestEmailConnection = async () => {
    setIsTestingEmail(true);
    setEmailTestSuccess(null);
    try {
      const target = gcalConfig.accountEmail || 'host@gmail.com';
      const res = await GoogleService.sendTestEmail(target);
      setEmailTestSuccess(res.message);
      showToast(`✓ Test email verified to ${target}`);
    } catch {
      showToast('❌ Test email failed. Check your password or connection.');
    } finally {
      setIsTestingEmail(false);
    }
  };

  const handleDeleteEvent = (id: string, title: string) => {
    const target = events.find(e => e.id === id);
    if (target) {
      setDeleteConfirmEvent(target);
    }
  };

  const handleCopyInvite = (event: ScheduledEvent) => {
    const text = SchedulerService.formatInvitation(event);
    navigator.clipboard.writeText(text);
    showToast('✓ Full meeting invite copied to clipboard!');
  };

  const handleLaunchSession = (event: ScheduledEvent) => {
    if (event.type === 'podcast') {
      if (onLaunchPodcastStudio) {
        onLaunchPodcastStudio(event.podcastFormat || 'vodcast-duo');
      } else {
        onNavigate('podcast-studio');
      }
    } else {
      if (onLaunchGroupMeeting) {
        onLaunchGroupMeeting(event.roomCode);
      } else {
        onNavigate('group-meeting');
      }
    }
  };

  // Filter events
  const filteredEvents = events.filter(evt => {
    // Tab filter
    if (activeTab === 'podcast' && evt.type !== 'podcast') return false;
    if (activeTab === 'meeting' && evt.type !== 'meeting') return false;
    if (activeTab === 'upcoming') {
      const isPast = new Date(evt.endTime).getTime() < Date.now();
      if (isPast) return false;
    }
    if (activeTab === 'today') {
      const evtDay = new Date(evt.startTime).toDateString();
      const today = new Date().toDateString();
      if (evtDay !== today) return false;
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = evt.title.toLowerCase().includes(q);
      const matchDesc = (evt.description || '').toLowerCase().includes(q);
      const matchRoom = (evt.roomCode || '').toLowerCase().includes(q);
      const matchGuest = evt.guests.some(g => g.name.toLowerCase().includes(q) || g.email.toLowerCase().includes(q));
      if (!matchTitle && !matchDesc && !matchRoom && !matchGuest) return false;
    }

    return true;
  });

  // Calculate Metrics
  const totalCount = events.length;
  const podcastCount = events.filter(e => e.type === 'podcast').length;
  const meetingCount = events.filter(e => e.type === 'meeting').length;
  const upcomingCount = events.filter(e => new Date(e.endTime).getTime() >= Date.now()).length;

  // Calendar generation helpers
  const currentMonthYearStr = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  const startOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
  const endOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
  const startDayOfWeek = startOfMonth.getDay(); // 0 is Sunday
  const daysInMonth = endOfMonth.getDate();

  const prevMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
  };

  const getEventsForDay = (dayNum: number) => {
    const targetDateStr = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), dayNum).toDateString();
    return events.filter(e => new Date(e.startTime).toDateString() === targetDateStr);
  };

  const formatCountdown = (startTimeIso: string) => {
    const diffMs = new Date(startTimeIso).getTime() - Date.now();
    if (diffMs < 0) {
      const endedMs = Math.abs(diffMs);
      if (endedMs < 3600000 * 2) return '🔴 Live / Just finished';
      return 'Completed';
    }
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `Starts in ${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Starts in ${diffHours}h ${diffMins % 60}m`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 ambient-gradient select-none">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand-surface border border-brand-border shadow-elevated text-sm text-brand-text font-medium animate-in fade-in slide-in-from-bottom-3 duration-200">
          <Sparkles className="w-4 h-4 text-brand-accent animate-pulse" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Header Card */}
      <div className="relative rounded-3xl p-6 md:p-8 bg-gradient-to-br from-brand-surface via-brand-surface to-brand-elevated border border-brand-border shadow-elevated overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-brand-accent/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 -top-20 w-60 h-60 bg-purple-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/25 text-brand-accent text-xs font-semibold shadow-sm">
              <CalendarCheck className="w-3.5 h-3.5" />
              <span>Google Calendar Integrated</span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold text-brand-text tracking-tight">
              Podcast & Meeting{' '}
              <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
                Scheduler
              </span>
            </h1>

            <p className="text-xs sm:text-sm text-brand-muted leading-relaxed">
              Schedule your upcoming podcast episodes and group conferences in advance. Generate invite links,
              sync directly with Google Calendar, and launch your local recording studio in one click.
            </p>
          </div>

          {/* Action CTAs */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => openCreateModal('podcast')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 text-white text-xs sm:text-sm font-semibold shadow-glow transition-all active:scale-95 cursor-pointer"
            >
              <Mic className="w-4 h-4" />
              <span>Schedule Podcast</span>
            </button>

            <button
              onClick={() => openCreateModal('meeting')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:brightness-110 text-white text-xs sm:text-sm font-semibold shadow-glow transition-all active:scale-95 cursor-pointer"
            >
              <Users className="w-4 h-4" />
              <span>Schedule Meeting</span>
            </button>

            <button
              onClick={handleSyncGoogleCalendar}
              disabled={isSyncing}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-surface border border-brand-border hover:bg-brand-elevated text-brand-text text-xs sm:text-sm font-medium transition-all active:scale-95 cursor-pointer"
              title="Synchronize all events with your Google Calendar"
            >
              <RefreshCw className={`w-4 h-4 text-brand-accent ${isSyncing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Sync Calendar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Row (4 Cards) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Scheduled */}
        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border/60 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-brand-muted uppercase tracking-wider">Total Scheduled</p>
            <h3 className="text-2xl font-black text-brand-text mt-1">{totalCount}</h3>
            <p className="text-[10px] text-brand-muted mt-0.5">{upcomingCount} upcoming</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-brand-accent/15 flex items-center justify-center text-brand-accent">
            <CalendarDays className="w-5 h-5" />
          </div>
        </div>

        {/* Podcasts */}
        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border/60 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-purple-400 uppercase tracking-wider">Podcasts</p>
            <h3 className="text-2xl font-black text-brand-text mt-1">{podcastCount}</h3>
            <p className="text-[10px] text-brand-muted mt-0.5">Studio & Vodcasts</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center text-purple-400">
            <Mic className="w-5 h-5" />
          </div>
        </div>

        {/* Meetings */}
        <div className="p-4 rounded-2xl bg-brand-surface border border-brand-border/60 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-blue-400 uppercase tracking-wider">Group Meetings</p>
            <h3 className="text-2xl font-black text-brand-text mt-1">{meetingCount}</h3>
            <p className="text-[10px] text-brand-muted mt-0.5">P2P Rooms & G-Meet</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center text-blue-400">
            <Users className="w-5 h-5" />
          </div>
        </div>

        {/* Google Calendar Status Card */}
        <div
          onClick={() => setIsConfigModalOpen(true)}
          className="p-4 rounded-2xl bg-brand-surface border border-brand-border/60 shadow-sm flex items-center justify-between cursor-pointer hover:border-brand-accent/40 transition-all"
        >
          <div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <p className="text-[11px] font-semibold text-emerald-400 uppercase tracking-wider">Gmail & Calendar</p>
            </div>
            <h3 className="text-sm font-bold text-brand-text mt-1 truncate max-w-[140px]">
              {gcalConfig.accountEmail || 'Setup Gmail ID'}
            </h3>
            <p className="text-[10px] text-brand-muted mt-0.5">Click to configure Outlook/SMTP</p>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400">
            <Mail className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Google Calendar Connected Banner */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-indigo-500/10 border border-emerald-500/25 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs sm:text-sm font-bold text-brand-text flex items-center gap-2">
              Gmail ID & Google Calendar Integration Active
              <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/20 text-emerald-400 font-mono font-bold">
                CONNECTED
              </span>
            </h4>
            <p className="text-[11px] text-brand-muted">
              Schedule podcasts & meetings, send automated calendar invites to guests via your Gmail ID, and sync schedules.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setIsConfigModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
          >
            <Mail className="w-3.5 h-3.5 text-brand-accent" />
            <span>Setup Gmail & Calendar</span>
          </button>
          <button
            onClick={handleSyncGoogleCalendar}
            disabled={isSyncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-sm transition-all cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
            <span>Sync Now</span>
          </button>
        </div>
      </div>

      {/* Controls: Search, Tabs & View Switcher */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Filter Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-brand-surface border border-brand-border/60 overflow-x-auto">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              activeTab === 'all'
                ? 'bg-brand-elevated text-brand-text font-bold shadow-sm'
                : 'text-brand-muted hover:text-brand-text'
            }`}
          >
            All Events ({totalCount})
          </button>
          <button
            onClick={() => setActiveTab('podcast')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              activeTab === 'podcast'
                ? 'bg-purple-600 text-white font-bold shadow-sm'
                : 'text-brand-muted hover:text-brand-text'
            }`}
          >
            <Mic className="w-3.5 h-3.5" />
            <span>Podcasts ({podcastCount})</span>
          </button>
          <button
            onClick={() => setActiveTab('meeting')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              activeTab === 'meeting'
                ? 'bg-blue-600 text-white font-bold shadow-sm'
                : 'text-brand-muted hover:text-brand-text'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Meetings ({meetingCount})</span>
          </button>
          <button
            onClick={() => setActiveTab('today')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              activeTab === 'today'
                ? 'bg-brand-elevated text-brand-text font-bold shadow-sm'
                : 'text-brand-muted hover:text-brand-text'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
              activeTab === 'upcoming'
                ? 'bg-brand-elevated text-brand-text font-bold shadow-sm'
                : 'text-brand-muted hover:text-brand-text'
            }`}
          >
            Upcoming
          </button>
        </div>

        {/* Right side: Search & View Toggle */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-3.5 h-3.5 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search title, guests, room..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-brand-surface border border-brand-border/60 text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent placeholder:text-brand-muted/70"
            />
          </div>

          <div className="flex items-center p-1 rounded-xl bg-brand-surface border border-brand-border/60 shrink-0">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                viewMode === 'list' ? 'bg-brand-elevated text-brand-text font-bold shadow-sm' : 'text-brand-muted'
              }`}
            >
              Agenda List
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                viewMode === 'calendar' ? 'bg-brand-elevated text-brand-text font-bold shadow-sm' : 'text-brand-muted'
              }`}
            >
              Month Calendar
            </button>
          </div>
        </div>
      </div>

      {/* MONTHLY CALENDAR VIEW */}
      {viewMode === 'calendar' && (
        <div className="p-6 rounded-3xl bg-brand-surface border border-brand-border/60 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-brand-border/40">
            <h3 className="text-lg font-bold text-brand-text flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brand-accent" />
              <span>{currentMonthYearStr}</span>
            </h3>

            <div className="flex items-center gap-2">
              <button
                onClick={prevMonth}
                className="p-1.5 rounded-lg bg-brand-elevated hover:bg-brand-border text-brand-text transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCalendarDate(new Date())}
                className="px-3 py-1 text-xs font-semibold rounded-lg bg-brand-elevated hover:bg-brand-border text-brand-text transition-colors cursor-pointer"
              >
                Today
              </button>
              <button
                onClick={nextMonth}
                className="p-1.5 rounded-lg bg-brand-elevated hover:bg-brand-border text-brand-text transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold text-brand-muted py-1">
            <span>Sun</span>
            <span>Mon</span>
            <span>Tue</span>
            <span>Wed</span>
            <span>Thu</span>
            <span>Fri</span>
            <span>Sat</span>
          </div>

          {/* Day Cells Grid */}
          <div className="grid grid-cols-7 gap-2">
            {/* Empty offset padding for days before 1st of month */}
            {Array.from({ length: startDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[90px] p-2 rounded-xl bg-brand-elevated/20 opacity-30" />
            ))}

            {/* Days of Month */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1;
              const dayEvents = getEventsForDay(dayNum);
              const isToday =
                new Date().getDate() === dayNum &&
                new Date().getMonth() === calendarDate.getMonth() &&
                new Date().getFullYear() === calendarDate.getFullYear();

              return (
                <div
                  key={`day-${dayNum}`}
                  className={`min-h-[90px] p-2 rounded-xl border transition-all flex flex-col justify-between ${
                    isToday
                      ? 'bg-brand-accent/10 border-brand-accent shadow-sm'
                      : 'bg-brand-surface hover:bg-brand-elevated/40 border-brand-border/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday ? 'bg-brand-accent text-white' : 'text-brand-text'
                      }`}
                    >
                      {dayNum}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[10px] font-mono px-1.5 rounded-full bg-brand-elevated text-brand-muted">
                        {dayEvents.length}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1 mt-1 overflow-y-auto max-h-[70px]">
                    {dayEvents.map(evt => (
                      <div
                        key={evt.id}
                        onClick={() => openEditModal(evt)}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate cursor-pointer transition-colors ${
                          evt.type === 'podcast'
                            ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                            : 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30'
                        }`}
                        title={`${evt.title} (${new Date(evt.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
                      >
                        {evt.type === 'podcast' ? '🎙️ ' : '👥 '}
                        {evt.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AGENDA / LIST VIEW */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {filteredEvents.length === 0 ? (
            <div className="p-12 text-center rounded-3xl bg-brand-surface border border-brand-border/60 space-y-4">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-accent/10 flex items-center justify-center text-brand-accent">
                <CalendarDays className="w-7 h-7" />
              </div>
              <div className="max-w-md mx-auto space-y-1">
                <h3 className="text-base font-bold text-brand-text">No Scheduled Events Found</h3>
                <p className="text-xs text-brand-muted">
                  {searchQuery
                    ? `No events match "${searchQuery}". Try a different keyword.`
                    : 'Plan your next podcast episode or online meeting with Google Calendar sync.'}
                </p>
              </div>
              <div className="flex justify-center gap-3 pt-2">
                <button
                  onClick={() => openCreateModal('podcast')}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold shadow-sm transition-all cursor-pointer"
                >
                  Schedule Podcast
                </button>
                <button
                  onClick={() => openCreateModal('meeting')}
                  className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-sm transition-all cursor-pointer"
                >
                  Schedule Meeting
                </button>
              </div>
            </div>
          ) : (
            filteredEvents.map(event => {
              const startDate = new Date(event.startTime);
              const isPodcast = event.type === 'podcast';
              const countdown = formatCountdown(event.startTime);

              return (
                <div
                  key={event.id}
                  className="p-5 md:p-6 rounded-2xl bg-brand-surface border border-brand-border/60 hover:border-brand-accent/30 shadow-sm transition-all space-y-4"
                >
                  {/* Card Top Row: Badges & Status */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Type Badge */}
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                          isPodcast
                            ? 'bg-purple-500/15 text-purple-400 border border-purple-500/25'
                            : 'bg-blue-500/15 text-blue-400 border border-blue-500/25'
                        }`}
                      >
                        {isPodcast ? <Mic className="w-3.5 h-3.5" /> : <Users className="w-3.5 h-3.5" />}
                        <span>{isPodcast ? 'Podcast Episode' : 'Group Meeting'}</span>
                      </span>

                      {/* Format / Platform Badge */}
                      <span className="px-2.5 py-1 rounded-lg bg-brand-elevated text-brand-muted text-xs font-mono">
                        {event.platform === 'podcast-studio'
                          ? `Studio (${event.podcastFormat || 'Vodcast'})`
                          : event.platform === 'meetsplit-room'
                          ? `Room: ${event.roomCode || 'MeetSplit'}`
                          : event.platform === 'google-meet'
                          ? 'Google Meet'
                          : 'Online Call'}
                      </span>

                      {/* Google Calendar Synced Badge */}
                      {event.googleCalendarSynced && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium border border-emerald-500/20">
                          <Check className="w-3 h-3" />
                          <span>Google Calendar</span>
                        </span>
                      )}

                      {/* Email Invites Sent Badge */}
                      {event.invitesSent && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-500/15 text-purple-400 text-xs font-medium border border-purple-500/20">
                          <Mail className="w-3 h-3" />
                          <span>Invited via Gmail</span>
                        </span>
                      )}

                      {/* Auto Record indicator */}
                      {event.autoRecord && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-500/15 text-rose-400 text-[10px] font-mono font-bold">
                          <Radio className="w-3 h-3 text-rose-500 animate-pulse" />
                          <span>AUTO-REC</span>
                        </span>
                      )}
                    </div>

                    {/* Date, Time & Countdown */}
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-elevated text-brand-text text-xs font-semibold">
                        <Clock className="w-3.5 h-3.5 text-brand-secondary" />
                        <span>
                          {startDate.toLocaleDateString([], { month: 'short', day: 'numeric', weekday: 'short' })} at{' '}
                          {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({event.durationMinutes}m)
                        </span>
                      </span>

                      <span className="px-2.5 py-1 rounded-full bg-brand-accent/15 text-brand-accent text-xs font-bold">
                        {countdown}
                      </span>
                    </div>
                  </div>

                  {/* Card Middle: Title, Description & Agenda */}
                  <div className="space-y-2">
                    <h3 className="text-base sm:text-lg font-bold text-brand-text tracking-tight">{event.title}</h3>

                    {event.description && (
                      <p className="text-xs sm:text-sm text-brand-muted leading-relaxed">{event.description}</p>
                    )}

                    {/* Agenda bullets if any */}
                    {event.agenda && event.agenda.length > 0 && (
                      <div className="pt-1">
                        <p className="text-[11px] font-semibold text-brand-muted uppercase tracking-wider mb-1">
                          Agenda / Talking Points:
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-brand-muted">
                          {event.agenda.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-1.5">
                              <span className="text-brand-accent shrink-0">•</span>
                              <span className="truncate">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Participants & Host Row */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-brand-border/40 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-brand-muted">Host:</span>
                      <span className="font-semibold text-brand-text bg-brand-elevated px-2 py-0.5 rounded">
                        {event.hostName} ({event.hostEmail})
                      </span>

                      {event.guests && event.guests.length > 0 && (
                        <>
                          <span className="text-brand-muted ml-2">Guests ({event.guests.length}):</span>
                          {event.guests.map(g => (
                            <span
                              key={g.id}
                              className="px-2 py-0.5 rounded bg-brand-elevated text-brand-text font-medium text-[11px]"
                              title={g.email}
                            >
                              {g.name} <span className="text-[9px] text-brand-muted">({g.role || 'guest'})</span>
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Bottom Action Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-brand-border/40">
                    {/* Primary Launch Action */}
                    <button
                      onClick={() => handleLaunchSession(event)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-white text-xs font-bold shadow-sm transition-all active:scale-95 cursor-pointer ${
                        isPodcast
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110'
                          : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:brightness-110'
                      }`}
                    >
                      <Play className="w-3.5 h-3.5 fill-current" />
                      <span>{isPodcast ? 'Launch Podcast Studio' : 'Enter Meeting Room'}</span>
                    </button>

                    {/* Secondary Actions */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Send Gmail Invite */}
                      <button
                        onClick={() => handleSendEmailInvite(event)}
                        disabled={isSendingEmail === event.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/30 text-purple-300 text-xs font-medium transition-colors cursor-pointer"
                        title="Dispatch calendar invite directly to guests using your Gmail"
                      >
                        <Mail className={`w-3.5 h-3.5 text-purple-400 ${isSendingEmail === event.id ? 'animate-bounce' : ''}`} />
                        <span>{event.invitesSent ? 'Re-send Email' : 'Email Guests'}</span>
                      </button>

                      {/* Add to Google Calendar Link */}
                      <button
                        onClick={() => SchedulerService.openInGoogleCalendar(event)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text text-xs font-medium transition-colors cursor-pointer"
                        title="Open event creation in Google Calendar"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-blue-400" />
                        <span>Google Calendar</span>
                      </button>

                      {/* Download .ICS */}
                      <button
                        onClick={() => SchedulerService.downloadIcs(event)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text text-xs font-medium transition-colors cursor-pointer"
                        title="Download .ICS invite file"
                      >
                        <Download className="w-3.5 h-3.5 text-brand-secondary" />
                        <span>.ICS File</span>
                      </button>

                      {/* Copy Invitation */}
                      <button
                        onClick={() => handleCopyInvite(event)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text text-xs font-medium transition-colors cursor-pointer"
                        title="Copy shareable invitation message"
                      >
                        <Copy className="w-3.5 h-3.5 text-brand-muted" />
                        <span>Copy Invite</span>
                      </button>

                      {/* Edit */}
                      <button
                        onClick={() => openEditModal(event)}
                        className="p-2 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-muted hover:text-brand-text transition-colors cursor-pointer"
                        title="Edit event details"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={() => setDeleteConfirmEvent(event)}
                        className="p-2 rounded-xl bg-brand-surface hover:bg-rose-500/10 border border-brand-border text-brand-muted hover:text-rose-400 transition-colors cursor-pointer"
                        title="Delete scheduled event"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* CREATE & EDIT MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-brand-surface border border-brand-border shadow-elevated p-6 space-y-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-brand-border/60">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    formType === 'podcast' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                  }`}
                >
                  {formType === 'podcast' ? <Mic className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="text-base font-bold text-brand-text">
                    {editingEventId ? 'Edit Scheduled Session' : 'Schedule New Session'}
                  </h3>
                  <p className="text-xs text-brand-muted">
                    Syncs automatically with your connected Google Calendar
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-muted hover:text-brand-text transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSaveEvent} className="space-y-5">
              {/* Type Switcher */}
              <div className="grid grid-cols-2 gap-3 p-1 rounded-2xl bg-brand-elevated border border-brand-border/60">
                <button
                  type="button"
                  onClick={() => {
                    setFormType('podcast');
                    setFormPlatform('podcast-studio');
                  }}
                  className={`flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    formType === 'podcast'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-brand-muted hover:text-brand-text'
                  }`}
                >
                  <Mic className="w-4 h-4" />
                  <span>Podcast Episode</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setFormType('meeting');
                    setFormPlatform('meetsplit-room');
                  }}
                  className={`flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    formType === 'meeting'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-brand-muted hover:text-brand-text'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>Group Meeting</span>
                </button>
              </div>

              {/* Title & Description */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">
                    {formType === 'podcast' ? 'Episode Title' : 'Meeting Title'} *
                  </label>
                  <input
                    type="text"
                    required
                    value={formTitle}
                    onChange={e => setFormTitle(e.target.value)}
                    placeholder={formType === 'podcast' ? 'e.g. Ep 15: AI Video Pipelines' : 'e.g. Weekly Product Strategy'}
                    className="w-full px-3.5 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">Description / Notes</label>
                  <textarea
                    rows={2}
                    value={formDescription}
                    onChange={e => setFormDescription(e.target.value)}
                    placeholder="Brief outline, talking points or expectations for attendees..."
                    className="w-full px-3.5 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  />
                </div>
              </div>

              {/* Date, Time & Duration */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={formDate}
                    onChange={e => setFormDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">Start Time *</label>
                  <input
                    type="time"
                    required
                    value={formTime}
                    onChange={e => setFormTime(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">Duration</label>
                  <select
                    value={formDuration}
                    onChange={e => setFormDuration(Number(e.target.value))}
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  >
                    <option value={15}>15 Minutes</option>
                    <option value={30}>30 Minutes</option>
                    <option value={45}>45 Minutes</option>
                    <option value={60}>1 Hour (60m)</option>
                    <option value={90}>1.5 Hours (90m)</option>
                    <option value={120}>2 Hours (120m)</option>
                  </select>
                </div>
              </div>

              {/* Platform / Mode specific options */}
              {formType === 'podcast' ? (
                <div className="p-4 rounded-2xl bg-purple-500/10 border border-purple-500/20 space-y-3">
                  <h4 className="text-xs font-bold text-purple-400 uppercase tracking-wider">Podcast Studio Settings</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-brand-text mb-1">Recording Mode</label>
                      <select
                        value={formPodcastFormat}
                        onChange={e => setFormPodcastFormat(e.target.value as PodcastFormat)}
                        className="w-full px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs text-brand-text focus:outline-none"
                      >
                        <option value="vodcast-duo">Duo Vodcast (Host & Guest Side-by-Side)</option>
                        <option value="vodcast-single">Solo Creator (Camera + Screen)</option>
                        <option value="vodcast-online">Online WebRTC Guests (P2P)</option>
                        <option value="audio-only">Audio-Only Studio Pro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-brand-text mb-1">Studio Room Code</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formRoomCode}
                          onChange={e => setFormRoomCode(e.target.value)}
                          placeholder="e.g. POD-8921"
                          className="flex-1 px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-mono text-brand-text focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setFormRoomCode(`POD-${Math.floor(1000 + Math.random() * 9000)}`)}
                          className="px-3 py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold"
                        >
                          Roll
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-2xl bg-blue-500/10 border border-blue-500/20 space-y-3">
                  <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider">Meeting Room Settings</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-brand-text mb-1">Platform</label>
                      <select
                        value={formPlatform}
                        onChange={e => setFormPlatform(e.target.value as SchedulePlatform)}
                        className="w-full px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs text-brand-text focus:outline-none"
                      >
                        <option value="meetsplit-room">MeetSplit P2P Room (Auto Split)</option>
                        <option value="google-meet">Google Meet</option>
                        <option value="zoom">Zoom Call</option>
                        <option value="custom">Custom Web Link</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-brand-text mb-1">
                        {formPlatform === 'meetsplit-room' ? 'Room Code' : 'Meeting Link URL'}
                      </label>
                      {formPlatform === 'meetsplit-room' ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={formRoomCode}
                            onChange={e => setFormRoomCode(e.target.value)}
                            placeholder="e.g. MS-7824"
                            className="flex-1 px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs font-mono text-brand-text focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setFormRoomCode(`MS-${Math.floor(1000 + Math.random() * 9000)}`)}
                            className="px-3 py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold"
                          >
                            Roll
                          </button>
                        </div>
                      ) : (
                        <input
                          type="url"
                          value={formCustomLink}
                          onChange={e => setFormCustomLink(e.target.value)}
                          placeholder="https://meet.google.com/..."
                          className="w-full px-3 py-2 rounded-xl bg-brand-surface border border-brand-border text-xs text-brand-text focus:outline-none"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Host Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">Host Name</label>
                  <input
                    type="text"
                    value={formHostName}
                    onChange={e => setFormHostName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-brand-text mb-1">Host Email</label>
                  <input
                    type="email"
                    value={formHostEmail}
                    onChange={e => setFormHostEmail(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                </div>
              </div>

              {/* Guest Attendees Section */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-brand-text">
                  Invite Guests / Participants ({formGuests.length})
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="Guest Name"
                    value={newGuestName}
                    onChange={e => setNewGuestName(e.target.value)}
                    className="flex-1 px-3 py-1.5 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                  <input
                    type="email"
                    placeholder="guest@example.com"
                    value={newGuestEmail}
                    onChange={e => setNewGuestEmail(e.target.value)}
                    className="flex-1 px-3 py-1.5 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                  <select
                    value={newGuestRole}
                    onChange={e => setNewGuestRole(e.target.value as any)}
                    className="px-3 py-1.5 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  >
                    <option value="guest">Guest</option>
                    <option value="co-host">Co-Host</option>
                    <option value="attendee">Attendee</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleAddGuest}
                    className="px-3 py-1.5 rounded-xl bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-elevated text-xs font-semibold cursor-pointer shrink-0"
                  >
                    + Add
                  </button>
                </div>

                {formGuests.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {formGuests.map(g => (
                      <span
                        key={g.id}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text"
                      >
                        <span>
                          {g.name} ({g.email})
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveGuest(g.id)}
                          className="text-brand-muted hover:text-rose-400 ml-1 cursor-pointer"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Agenda Items Section */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-brand-text">Agenda / Talking Points</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add agenda topic..."
                    value={newAgendaItem}
                    onChange={e => setNewAgendaItem(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAgendaItem();
                      }
                    }}
                    className="flex-1 px-3 py-1.5 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleAddAgendaItem}
                    className="px-3 py-1.5 rounded-xl bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-elevated text-xs font-semibold cursor-pointer shrink-0"
                  >
                    + Add Point
                  </button>
                </div>

                {formAgenda.length > 0 && (
                  <ul className="space-y-1 pt-1">
                    {formAgenda.map((item, idx) => (
                      <li
                        key={idx}
                        className="flex items-center justify-between px-3 py-1 rounded-lg bg-brand-elevated/40 text-xs text-brand-muted"
                      >
                        <span className="truncate">
                          {idx + 1}. {item}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAgendaItem(idx)}
                          className="text-brand-muted hover:text-rose-400 cursor-pointer"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Toggles: Google Calendar Sync, Auto Email & Auto Record */}
              <div className="p-3 rounded-2xl bg-brand-elevated/60 border border-brand-border/40 space-y-2">
                <label className="flex items-center justify-between text-xs font-medium text-brand-text cursor-pointer">
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-400" />
                    <span>Auto-email calendar invite to guests from your Gmail</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formSendEmailInvites}
                    onChange={e => setFormSendEmailInvites(e.target.checked)}
                    className="rounded text-brand-accent focus:ring-brand-accent h-4 w-4"
                  />
                </label>

                <label className="flex items-center justify-between text-xs font-medium text-brand-text cursor-pointer">
                  <span className="flex items-center gap-2">
                    <CalendarCheck className="w-4 h-4 text-emerald-400" />
                    <span>Sync & add to Google Calendar</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formSyncToGoogle}
                    onChange={e => setFormSyncToGoogle(e.target.checked)}
                    className="rounded text-brand-accent focus:ring-brand-accent h-4 w-4"
                  />
                </label>

                <label className="flex items-center justify-between text-xs font-medium text-brand-text cursor-pointer">
                  <span className="flex items-center gap-2">
                    <Radio className="w-4 h-4 text-rose-400" />
                    <span>Auto-record video & split participants when room starts</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={formAutoRecord}
                    onChange={e => setFormAutoRecord(e.target.checked)}
                    className="rounded text-brand-accent focus:ring-brand-accent h-4 w-4"
                  />
                </label>
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-brand-border/60">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-brand-accent to-indigo-600 hover:brightness-110 text-white text-xs font-bold shadow-glow transition-all active:scale-95 cursor-pointer"
                >
                  <CalendarCheck className="w-4 h-4" />
                  <span>{editingEventId ? 'Save Changes' : 'Schedule & Sync'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GMAIL & GOOGLE CALENDAR CONFIG MODAL (OUTLOOK & SMTP STYLE) */}
      {isConfigModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-3xl bg-brand-surface border border-brand-border shadow-elevated p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-brand-border/60">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500/20 to-blue-500/20 text-emerald-400 flex items-center justify-center border border-emerald-500/30">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-brand-text">Gmail & Google Calendar Setup</h3>
                  <p className="text-[11px] text-brand-muted">Configure your Gmail ID to dispatch invites and sync schedules</p>
                </div>
              </div>
              <button
                onClick={() => setIsConfigModalOpen(false)}
                className="p-1.5 rounded-lg bg-brand-elevated text-brand-muted hover:text-brand-text"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="flex items-center gap-1 p-1 rounded-xl bg-brand-elevated border border-brand-border/60">
              <button
                type="button"
                onClick={() => setConfigTab('profile')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  configTab === 'profile' ? 'bg-brand-surface text-brand-text shadow-sm' : 'text-brand-muted'
                }`}
              >
                Gmail Account
              </button>
              <button
                type="button"
                onClick={() => setConfigTab('smtp')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  configTab === 'smtp' ? 'bg-brand-surface text-brand-text shadow-sm' : 'text-brand-muted'
                }`}
              >
                Outlook / SMTP
              </button>
              <button
                type="button"
                onClick={() => setConfigTab('automation')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  configTab === 'automation' ? 'bg-brand-surface text-brand-text shadow-sm' : 'text-brand-muted'
                }`}
              >
                Automation
              </button>
            </div>

            {/* TAB 1: Profile & Gmail ID */}
            {configTab === 'profile' && (
              <div className="space-y-4 text-xs">
                <div>
                  <label className="block font-semibold text-brand-text mb-1">Your Gmail Address (Sender ID) *</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={gcalConfig.accountEmail || ''}
                      onChange={e => {
                        const email = e.target.value;
                        const updated = SchedulerService.saveGoogleCalendarConfig({ accountEmail: email });
                        GoogleService.connectAccount(email, gcalConfig.accountName);
                        setGcalConfig(updated);
                      }}
                      placeholder="e.g. yourname@gmail.com"
                      className="w-full pl-9 pr-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold text-brand-text mb-1">Host Display Name</label>
                  <input
                    type="text"
                    value={gcalConfig.accountName || ''}
                    onChange={e => {
                      const name = e.target.value;
                      const updated = SchedulerService.saveGoogleCalendarConfig({ accountName: name });
                      GoogleService.saveAccountInfo({ name });
                      setGcalConfig(updated);
                    }}
                    placeholder="e.g. Mahendra (MeetSplit Host)"
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-brand-text mb-1">Primary Calendar Name</label>
                  <input
                    type="text"
                    value={gcalConfig.calendarName || ''}
                    onChange={e => {
                      const name = e.target.value;
                      const updated = SchedulerService.saveGoogleCalendarConfig({ calendarName: name });
                      setGcalConfig(updated);
                    }}
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border text-xs text-brand-text focus:outline-none"
                  />
                </div>

                <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-semibold text-emerald-400">Google Account Connected</span>
                  </div>
                  <span className="text-[11px] text-brand-muted font-mono">{gcalConfig.accountEmail}</span>
                </div>
              </div>
            )}

            {/* TAB 2: Outlook / SMTP App Password */}
            {configTab === 'smtp' && (
              <div className="space-y-4 text-xs">
                <div className="p-3 rounded-2xl bg-brand-elevated/70 border border-brand-border/40 space-y-1.5">
                  <div className="flex items-center gap-1.5 font-semibold text-brand-text">
                    <Key className="w-3.5 h-3.5 text-brand-accent" />
                    <span>How to get Gmail App Password (like in Outlook):</span>
                  </div>
                  <p className="text-[11px] text-brand-muted leading-relaxed">
                    1. Open your Google Account (<span className="text-brand-accent">myaccount.google.com</span>)<br />
                    2. Go to <strong>Security → 2-Step Verification → App passwords</strong><br />
                    3. Generate an App Password for <strong>"MeetSplit AI"</strong> and paste the 16-character code below.
                  </p>
                </div>

                <div>
                  <label className="block font-semibold text-brand-text mb-1">Gmail App Password</label>
                  <input
                    type="password"
                    value={smtpPasswordInput}
                    onChange={e => {
                      setSmtpPasswordInput(e.target.value);
                      GoogleService.saveAccountInfo({
                        emailConfig: {
                          ...GoogleService.getAccountInfo().emailConfig,
                          smtpPassword: e.target.value
                        }
                      });
                    }}
                    placeholder="•••• •••• •••• ••••"
                    className="w-full px-3 py-2 rounded-xl bg-brand-elevated border border-brand-border font-mono text-xs text-brand-text focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold text-brand-text mb-1">SMTP Server</label>
                    <input
                      type="text"
                      readOnly
                      value="smtp.gmail.com"
                      className="w-full px-3 py-2 rounded-xl bg-brand-elevated/50 border border-brand-border text-xs text-brand-muted cursor-not-allowed font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-brand-text mb-1">Port</label>
                    <input
                      type="text"
                      readOnly
                      value="587 (TLS)"
                      className="w-full px-3 py-2 rounded-xl bg-brand-elevated/50 border border-brand-border text-xs text-brand-muted cursor-not-allowed font-mono"
                    />
                  </div>
                </div>

                {/* Test Connection Button */}
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={handleTestEmailConnection}
                    disabled={isTestingEmail}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text font-semibold transition-all cursor-pointer"
                  >
                    <Send className={`w-3.5 h-3.5 text-brand-accent ${isTestingEmail ? 'animate-bounce' : ''}`} />
                    <span>{isTestingEmail ? 'Testing Connection...' : 'Send Test Verification Email'}</span>
                  </button>

                  {emailTestSuccess && (
                    <p className="text-[11px] text-emerald-400 font-medium text-center mt-2 animate-in fade-in">
                      ✓ {emailTestSuccess}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* TAB 3: Automation Preferences */}
            {configTab === 'automation' && (
              <div className="space-y-3 text-xs">
                <div className="p-3 rounded-2xl bg-brand-elevated/60 border border-brand-border/40 space-y-3">
                  <label className="flex items-center justify-between font-medium text-brand-text cursor-pointer">
                    <div className="space-y-0.5">
                      <p className="font-semibold">Auto-email guests on schedule creation</p>
                      <p className="text-[11px] text-brand-muted">Automatically sends calendar invite with room link & .ICS</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formSendEmailInvites}
                      onChange={e => setFormSendEmailInvites(e.target.checked)}
                      className="rounded text-brand-accent focus:ring-brand-accent h-4 w-4"
                    />
                  </label>

                  <div className="pt-2 border-t border-brand-border/40">
                    <label className="flex items-center justify-between font-medium text-brand-text cursor-pointer">
                      <div className="space-y-0.5">
                        <p className="font-semibold">Auto-sync with Google Calendar</p>
                        <p className="text-[11px] text-brand-muted">Keeps local schedule aligned with your Google Calendar</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={gcalConfig.autoSyncNewEvents}
                        onChange={e => {
                          const updated = SchedulerService.saveGoogleCalendarConfig({ autoSyncNewEvents: e.target.checked });
                          setGcalConfig(updated);
                        }}
                        className="rounded text-brand-accent focus:ring-brand-accent h-4 w-4"
                      />
                    </label>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-brand-elevated text-brand-muted space-y-1">
                  <p className="font-semibold text-brand-text">Live Sync Status:</p>
                  <p>Last synced: {new Date(gcalConfig.lastSyncedAt || Date.now()).toLocaleTimeString()}</p>
                </div>
              </div>
            )}

            {/* Modal Footer Actions */}
            <div className="flex items-center justify-between pt-3 border-t border-brand-border/60">
              <button
                type="button"
                onClick={() => {
                  GoogleService.disconnectAccount();
                  const updated = SchedulerService.saveGoogleCalendarConfig({ isConnected: false });
                  setGcalConfig(updated);
                  showToast('Disconnected Google Account');
                  setIsConfigModalOpen(false);
                }}
                className="text-xs text-rose-400 hover:underline cursor-pointer"
              >
                Disconnect
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsConfigModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await handleSyncGoogleCalendar();
                    setIsConfigModalOpen(false);
                  }}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-brand-accent to-indigo-600 hover:brightness-110 text-white text-xs font-bold shadow-glow transition-all active:scale-95 cursor-pointer"
                >
                  Save & Sync
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION POPUP MODAL */}
      {deleteConfirmEvent && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl bg-brand-surface border border-brand-border shadow-elevated p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/25 text-rose-500 flex items-center justify-center shrink-0">
                <Trash2 className="w-6 h-6 animate-pulse" />
              </div>

              <div className="space-y-1">
                <h3 className="text-base font-bold text-brand-text">Delete Scheduled Event?</h3>
                <p className="text-xs text-brand-muted leading-relaxed">
                  Are you sure you want to delete <span className="font-semibold text-brand-text">"{deleteConfirmEvent.title}"</span>?
                </p>
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-brand-elevated/60 border border-brand-border/40 text-xs space-y-1.5">
              <div className="flex items-center justify-between text-brand-muted">
                <span>Type:</span>
                <span className="font-bold text-brand-text capitalize">
                  {deleteConfirmEvent.type === 'podcast' ? '🎙️ Podcast Episode' : '👥 Group Meeting'}
                </span>
              </div>
              <div className="flex items-center justify-between text-brand-muted">
                <span>Date & Time:</span>
                <span className="font-semibold text-brand-text">
                  {new Date(deleteConfirmEvent.startTime).toLocaleString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {deleteConfirmEvent.roomCode && (
                <div className="flex items-center justify-between text-brand-muted">
                  <span>Room Code:</span>
                  <span className="font-mono text-brand-accent">{deleteConfirmEvent.roomCode}</span>
                </div>
              )}
              <div className="text-[11px] text-rose-400/90 pt-1 border-t border-brand-border/40">
                ⚠️ This will permanently remove the event from your schedule and Google Calendar sync.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmEvent(null)}
                className="px-4 py-2 rounded-xl bg-brand-elevated hover:bg-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const updated = SchedulerService.deleteEvent(deleteConfirmEvent.id);
                  setEvents(updated);
                  showToast(`✓ "${deleteConfirmEvent.title}" removed from schedule`);
                  setDeleteConfirmEvent(null);
                  if (editingEventId === deleteConfirmEvent.id) {
                    setIsModalOpen(false);
                  }
                }}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold shadow-lg shadow-rose-600/30 transition-all active:scale-95 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Yes, Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulerScreen;
